import React, { useEffect, useState } from "react";
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Modal,
  Row,
  CustomInput,
  Spinner,
  Table
} from "reactstrap";

import moment from "moment";
import PropTypes from "prop-types";
import { Filter } from "react-feather";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import SearchInput from "../SearchInput";
import OrderFilterComponent from "../Filter";
import { ConfirmDialog } from "../ModalDialog";
import PaginationComponent from "../Pagination";
import {
  BUTTON_CUSTOMS_CLEARED,
  BUTTON_HEALD_AT_CUSTOMS,
  BUTTON_PRINT_AIDUK_LABEL,
  BUTTON_PRINT_ESNAD_LABEL
} from "../../constants";
import SimpleSearch from "components/SimpleSearch";
import { format } from "utils/numberUtil";

const CustomButton = ({
  label,
  onPress,
  disabled = false,
  icon = null,
  isLoading = false,
  color = "primary",
  style = {}
}) => (
  <Button
    disabled={isLoading || disabled}
    color={color}
    onClick={() => !isLoading && onPress()}
    size="sm"
    style={{
      margin: "5px",
      border: 0,
      ...style
    }}>
    {icon && (
      <span className="btn-wrapper--icon">
        <FontAwesomeIcon icon={["fas", icon]} className="text" />
      </span>
    )}
    <span className="btn-wrapper--label">
      {isLoading ? "Loading..." : label}
    </span>
  </Button>
);

export default function DataTable({
  // Page props
  currentPage = "",
  setCurrentPage = null,
  showPagination = true,
  headerClassName = "", // classname for header

  // Data props
  filterType = "",
  loading = false,
  isLoading = false,
  tableLabel = "",
  column = [],
  data = [],
  count = "",

  // Add button action
  showAddButton = false,
  addButtonIcon = "plus-circle",
  addButtonLabel = "Add",
  addButtonLoader = false,
  onAdd = () => { },

  // Row actions
  showActions = false,
  onActionClick = () => { },

  // Edit action
  isEdit = false,
  onEdit = () => { },
  selectRowColumn = null,

  // View action
  isView = false,
  onView = () => { },

  // Delete action
  isDelete = false,
  deleteActionIcon = "times",
  onDelete = () => { },
  deleteButtonLabel = "Delete",
  deleteConfirmLabel = "",
  renderCustomDeleteModal = () => null,
  disabledCancel = false,

  // Row selection action
  selectableRow = false,
  selectionKey,
  // selectedIds = [],
  setSelectedIds = () => { },

  containerStyle = {},

  // Search
  showSearch = false,
  showSearchComponent = false,
  searchOptions = null,
  onHandleSearch = () => { },
  onClearSearch = () => { },
  customSearchComponent = null,

  // Filter
  showFilter = false,
  isFilterApply = false,
  onClearFilter = () => { },
  handleFilter = () => { },
  FilterComponent = OrderFilterComponent,

  // Export button
  showExportButton = false,
  onExportClick = () => { },

  // Mark Delivered button
  showMarkDeliveredButton = false,
  onMarkDeliveredClick = () => { },
  showMarkDeliveredLoading = false,

  // Print button
  showPrintButton = false,
  printButtonIcon = "print",
  printButtonLabel = "Print Label",
  onPrintClick = () => { },

  showAmazonPrintLabelButton = false,
  onPrintAmazonLabel,

  buttons = [],
  buttonCallback = () => { },
  onHeldAtCustoms = () => { },
  onCustomsCleared = () => { },
  onPrintAidukLabel = () => { },
  onPrintEsnadLabel = () => { },

  showPageLimit = false,
  limit = 10,
  onLimitChange = () => { },

  isAddAction = false,
  onAddAction = () => { },
  onRemoveAction = () => { },
  isRemoveAction = false,

  showHeader = true,

  onRowClick = () => { },

  // COD status props
  showCODStatusButton = false,
  onRemitted = () => { },
  onNotRemitted = () => { },
  onColumnClick = () => { },
  showRemitted = false,
  showNotRemitted = false,

  // Print 3PL Label button
  showPrint3PLLabelButton = false,
  onPrint3PLLabelClick = () => { },

  // Change 3PL button
  showChange3PLButton = false,
  onChange3PLClick = () => { },

  // change clearance process button
  showClearanceBtn = false,
  onClearanceBtn = () => { },

  onPrintAction = () => { },
  isPrintAction = false,
  startClearanceLoading = false,

  component
}) {
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [isSelectAll, setSelectAll] = useState(false);
  const [openConfirmationModal, setConfirmationModal] = useState(false);
  const [rowToDelete, setRow] = useState({});

  const [isFilterActive, setIsFilterActive] = useState(isFilterApply);
  const [filterOpen, setFilterOpen] = useState(false);
  const [clearFilter, setClearFilter] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [formatModalData, setFormatModalData] = useState(false);
  const [modalData, setModalData] = useState("");

  const clearDropdownData = () => {
    setSelectAll(false);
    setSelectedRecords([]);
    setSelectedIds([]);
  };

  // On change of page change
  useEffect(() => {
    if (data?.length) {
      clearDropdownData();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    setIsFilterActive(isFilterApply);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFilterApply]);

  /**
   * On check of check box
   */
  const onCheckedAll = () => {
    // Update checkobox
    let isChecked = !isSelectAll;
    setSelectAll(isChecked);

    let records = [];

    // Check all checkbox
    if (isChecked) {
      records = data.map((row) => row[selectionKey]);
      records = records.concat(selectedRecords);
      records = [...new Set(records)];
    }

    // Set selected records
    setSelectedRecords(records);
    setSelectedIds(records);
  };

  /**
   * On check box check/un-check of row
   * @param {*} row 
   */
  const onChecked = (row) => {
    // Find selected row index
    const index = selectedRecords.indexOf(row[selectionKey]);
    let records = selectedRecords;

    // Add or remove selection
    if (index > -1) {
      records.splice(index, 1);
      records = [...records];
    } else {
      records = [...records, row[selectionKey]];
    }

    // Set selected records
    setSelectedRecords(records);
    setSelectedIds(records);

    // Is select all is check then un-check
    if (isSelectAll) {
      setSelectAll(false);
    }
  };

  /**
   * Table button click
   * @param {*} buttonKey 
   */
  const customButtonClick = (button) => {
    switch (button.key) {
      case BUTTON_CUSTOMS_CLEARED:
        onCustomsCleared();
        break;
      case BUTTON_HEALD_AT_CUSTOMS:
        onHeldAtCustoms();
        break;
      case BUTTON_PRINT_AIDUK_LABEL:
        onPrintAidukLabel();
        break;
      case BUTTON_PRINT_ESNAD_LABEL:
        onPrintEsnadLabel();
        break;
      default:
        if (button.callback) {
          button.callback();
        } else {
          buttonCallback(button.key);
        }
        break;
    }
  };

  /**
   * Toggle data modal state
   */
  const onToggleModal = () => {
    setShowModal(!showModal);
  };

  /**
   * Show Data modal, show column data in modal
   * @param {*} data
   */
  const onShowModal = (data, formatData) => {
    setModalData(data);
    setFormatModalData(formatData);
    setShowModal(true);
  };

  /**
   * Render badge cell
   * @param {*} col 
   * @param {*} row 
   * @returns 
   */
  const renderBadge = (col, row) => {
    // If badge option are given fetch take color from them
    if (col.badgeOptions) {
      // Default color
      let badgeColor = "success";

      // Fetch badge color for current row value 
      let label = row[col.key];
      // eslint-disable-next-line eqeqeq
      const option = col.badgeOptions.find((item) => item.value == row[col.key]);
      if (option) {
        badgeColor = option.color;
        label = option.label || option.value;
      }
      return (<Badge color={`neutral-${badgeColor}`} className={`text-${badgeColor} px-4`}>{label}</Badge>);
    }
    // Success/Failed Badge
    else if (row[col.key] === 1) {
      return (<Badge color="neutral-success" className="text-success px-4">Success</Badge>);
    } else {
      return (<Badge color="neutral-danger" className="text-danger px-4">Failed</Badge>);
    }
  };

  /**
   * Render badge cell
   * @param {*} col 
   * @param {*} row 
   * @returns 
   */
  const renderNumberBadge = (col, row) => {
    try {
      if (Number(row[col.key]) < 0) {
        return (<span className="text-danger">{row[col.key]}</span>);
      } else {
        return (<span className="text-success">{row[col.key]}</span>);
      }
    } catch (error) {
      console.error("table: ", error);
    }
  };

  return (
    <>
      <Card
        className="card-box"
        style={{ overflow: "hidden", ...containerStyle }}>
        {showHeader && (
          <div className={`card-header py-3 ${headerClassName || ""}`}>
            <div className="card-header--title font-size-lg">{tableLabel}</div>
            {/* Search order component */}
            {showSearchComponent ? (
              <>
                {customSearchComponent ? customSearchComponent : (
                  <div>
                    <SearchInput
                      searchOptions={searchOptions}
                      onHandleSearch={onHandleSearch}
                      onClearSearch={onClearSearch}
                    />
                  </div>
                )}
              </>
            ) : showSearch ? (
              <SimpleSearch onChange={onHandleSearch} />
            ) : null}
            {component ? component : null}
            {showFilter || showAddButton ? (
              <div className="card-header--actions">
                <div
                  className="d-flex justify-content-between p-4 px-4 py-3"
                  style={{ paddingRight: 0 }}>
                  {/* Filter order component */}
                  {showFilter && (
                    <div style={{ paddingRight: 20 }}>
                      <FilterComponent
                        filterType={filterType}
                        show={filterOpen}
                        clearFilter={clearFilter}
                        handleFilter={(filter) => {
                          setIsFilterActive(true);
                          handleFilter(filter);
                        }}
                        onToggle={() => setFilterOpen(!filterOpen)}
                      />
                      <Button
                        outline
                        color="primary"
                        onClick={() => {
                          setFilterOpen(true);
                        }}>
                        <Filter className="w-70" />
                        {isFilterActive && (
                          <FontAwesomeIcon
                            onClick={(event) => {
                              event.preventDefault();
                              setIsFilterActive(false);
                              setClearFilter(!clearFilter);
                              onClearFilter();
                            }}
                            icon={["fas", "times-circle"]}
                            style={{
                              position: "relative",
                              top: "-17px",
                              left: "27px",
                              color: "red"
                            }}
                          />
                        )}
                      </Button>
                    </div>
                  )}
                  {showAddButton && (
                    <Button
                      color="primary"
                      onClick={onAdd}
                      size="sm"
                      style={{ color: "#000000" }}
                      disabled={addButtonLoader}>
                      {addButtonIcon !== "" && (
                        <span className="btn-wrapper--icon">
                          <FontAwesomeIcon
                            icon={["fas", addButtonIcon]}
                            className="text"
                          />
                        </span>
                      )}
                      <span className="btn-wrapper--label">
                        {addButtonLoader ? "loading..." : addButtonLabel}
                      </span>
                    </Button>
                  )}
                </div>
              </div>
            ) : null}
          </div>
        )}
        <div className="d-flex justify-content-between px-4 py-3">
          {showPageLimit ? (
            <div className="d-flex align-items-center mr-2">
              <span>Show</span>
              <select
                className="mx-2 form-control form-control-sm"
                style={{
                  minWidth: 72
                }}
                value={limit}
                onChange={(event) => onLimitChange(event.target.value)}>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={150}>150</option>
                <option value={200}>200</option>
              </select>
              <span>entries</span>
            </div>
          ) : (
            <div className="d-flex align-items-center"></div>
          )}
          <div className="align-item">
            {showClearanceBtn && (
              <CustomButton
                style={{ color: "#000000" }}
                label={"Start Clearance Process"}
                onPress={onClearanceBtn}
                isLoading={startClearanceLoading}
              />
            )}

            {showMarkDeliveredButton && (
              <>
                <CustomButton
                  style={{ color: "#000000" }}
                  label={"Mark as Delivered"}
                  onPress={onMarkDeliveredClick}
                  isLoading={showMarkDeliveredLoading}
                />
              </>
            )}
            {(showCODStatusButton || showRemitted || showNotRemitted) && (
              <>
                {(showNotRemitted || showCODStatusButton) && (
                  <CustomButton
                    style={{ color: "#000000" }}
                    label={"Not Remitted"}
                    onPress={onNotRemitted}
                  />
                )}
                {(showRemitted || showCODStatusButton) && (
                  <CustomButton
                    style={{ color: "#000000" }}
                    label={"Remitted"}
                    onPress={onRemitted} />
                )}
              </>
            )}
            {/* Show dynamic button sent from parent page */}
            {buttons.map((button, index) => (
              <CustomButton
                style={{ color: "#000000" }}
                key={index}
                label={button.label}
                icon={button.icon}
                isLoading={button.isLoading}
                onPress={
                  () => !button.isLoading
                    && button.onClick ? button.onClick() : customButtonClick(button)
                }
              />
            ))}
            {showAmazonPrintLabelButton && (
              <CustomButton
                style={{ color: "#000000" }}
                icon={"print"}
                label={"Print Amazon Label"}
                onPress={onPrintAmazonLabel} />
            )}
            {showChange3PLButton && (
              <CustomButton
                style={{ color: "#000000" }}
                label={"Change 3PL"}
                onPress={onChange3PLClick} />
            )}
            {showPrint3PLLabelButton && (
              <CustomButton
                style={{ color: "#000000" }}
                label={"Print 3PL Label"}
                onPress={onPrint3PLLabelClick}
                icon={"print"}
              />
            )}
            {showPrintButton && (
              <CustomButton
                style={{ color: "#000000" }}
                label={printButtonLabel}
                onPress={onPrintClick}
                icon={printButtonIcon}
              />
            )}
            {showExportButton && (
              <CustomButton
                style={{ color: "#000000" }}
                label={"Export"}
                onPress={onExportClick}
                icon={"arrow-up"}
              />
            )}
          </div>
        </div>
        <div className="divider" />
        {isLoading || loading ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center"
            }}>
            <Spinner
              color="primary"
              type="grow"
              style={{
                width: "3rem",
                height: "3rem",
                margin: "10px 0px"
              }}
            />
            <div className="title font-size-lg">Loading...</div>
          </div>
        ) : (
          <div className="table-responsive-md" style={{ overflowX: "auto" }}>
            {column.length && data.length ? (
              <Table hover className="text-nowrap mb-0">
                <thead>
                  <tr>
                    {selectableRow && selectionKey && (
                      <th
                        className="bg-white text-center"
                        style={{ width: "5%" }}>
                        <CustomInput
                          type="checkbox"
                          id={"checkbox-all"}
                          className="align-self-start"
                          label=""
                          checked={isSelectAll}
                          onChange={onCheckedAll}
                        />
                      </th>
                    )}
                    {column.map((col, idx) => (
                      <th key={idx} className="bg-white text-center">
                        {col.header}
                      </th>
                    ))}
                    {showActions && (
                      <th className="bg-white text-center">actions</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {data.map((row, index) => (
                    <tr
                      key={index}
                      onClick={() => (onRowClick ? onRowClick(row) : null)}>
                      {selectableRow && selectionKey && (
                        <td className="text-center">
                          <CustomInput
                            type="checkbox"
                            id={"checkbox-" + index}
                            className="align-self-start"
                            label=""
                            checked={selectedRecords.includes(
                              row[selectionKey]
                            )}
                            onChange={() => onChecked(row)}
                          />
                        </td>
                      )}

                      {column.map((col, idx) =>
                        // Check column type is datetime
                        col.type === "date" && row[col.key] ? (
                          <td key={idx} className="text-center">
                            {moment(row[col.key]).format(
                              col.format ? col.format : "DD/MM/YYYY"
                            )}
                          </td>
                        ) : col.type === "datetime" && row[col.key] ? (
                          <td key={idx} className="text-center">
                            {moment(row[col.key]).format(
                              col.format ? col.format : "DD/MM/YYYY HH:mm"
                            )}
                          </td>
                        ) : col.type === "modal" ? (
                          // Show data in modal
                          <td key={idx} className="text-center">
                            {/* eslint-disable-next-line */}
                            <a
                              onClick={() =>
                                onShowModal(row[col.key], col.formatData)
                              }
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: "#0086e6", cursor: "pointer" }}>
                              {row[col.key] && col.linkTitle
                                ? col.linkTitle
                                : ""}
                            </a>
                          </td>
                        ) : col.type === "link" && row[col.key] ? (
                          <td key={idx} className="text-center">
                            <a
                              href={row[col.key]}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: "#0086e6" }}>
                              {col.linkTitle ? col.linkTitle : "Link"}
                            </a>
                          </td>
                        ) : col.type === "action" && row[col.key] ? (
                          <td key={idx} className="text-center table-cell">
                            <CustomButton
                              label={col.actionTitle}
                              color={col.actionColor}
                              onPress={() => onActionClick(col.actionId, row)}
                            />
                          </td>
                        ) : col.type === "badge" ? (
                          <td key={idx} className="text-center table-cell">
                            {renderBadge(col, row)}
                          </td >
                        ) : col.type === "percentage" ? (
                          <td key={idx} className="text-center table-cell">
                            {`${row[col.key]}%`}
                          </td >
                        ) : col.type === "number" ? (
                          <td key={idx} className="text-center table-cell">
                            {`${format(row[col.key], col.decimals)}`}
                          </td >
                        ) : col.type === "numberBadge" ? (
                          <td key={idx} className="text-center table-cell">
                            {renderNumberBadge(col, row)}
                          </td >
                        ) : col.type === "summaryStatus" && row[col.key] ? (
                          <td
                            key={idx}
                            className="text-center  table-cell"
                            onClick={() => onColumnClick(col.header, row)}>
                            {row[col.key]}
                          </td>
                        ) : // Check all columns are selectable of given (to open view page)
                          !selectRowColumn ||
                            selectRowColumn.includes(col.key) ? (
                            <td
                              key={idx}
                              className="text-center table-cell"
                              onClick={() => onView(row, col)}>
                              {row[col.key] &&
                                typeof row[col.key] === "object" &&
                                Object.entries(row[col.key]).length
                                ? row[col.key][Object.keys(row[col.key])[0]]
                                : row[col.key]}
                            </td>
                          ) : (
                            <td key={idx} className="text-center table-cell">
                              {row[col.key] &&
                                typeof row[col.key] === "object" &&
                                Object.entries(row[col.key]).length
                                ? row[col.key][Object.keys(row[col.key])[0]]
                                : row[col.key]}
                            </td>
                          )
                      )
                      }
                      {
                        showActions && (
                          <td className="text-center">
                            {isEdit && (
                              <Button
                                color="neutral-first"
                                className="mx-1 shadow-none d-30 border-0 p-0 d-inline-flex align-items-center justify-content-center"
                                onClick={() => onEdit(row)}>
                                <FontAwesomeIcon
                                  icon={["far", "edit"]}
                                  className="font-size-sm"
                                />
                              </Button>
                            )}
                            {isDelete && (
                              <Button
                                color="neutral-danger"
                                onClick={() => {
                                  setRow(row);
                                  setConfirmationModal(true);
                                }}
                                className="mx-1 shadow-none d-30 border-0 p-0 d-inline-flex align-items-center justify-content-center">
                                <FontAwesomeIcon
                                  icon={["fas", deleteActionIcon]}
                                  className="font-size-sm"
                                />
                              </Button>
                            )}

                            {isView && (
                              <Button
                                color="neutral-second"
                                onClick={() => onView(row)}
                                className="mx-1 shadow-none d-30 border-0 p-0 d-inline-flex align-items-center justify-content-center">
                                <FontAwesomeIcon
                                  icon={["fas", "eye"]}
                                  className="font-size-sm"
                                />
                              </Button>
                            )}

                            {isAddAction && (
                              <Button
                                color="neutral-second"
                                onClick={() => onAddAction(row)}
                                className="mx-1 shadow-none d-30 border-0 p-0 d-inline-flex align-items-center justify-content-center">
                                <FontAwesomeIcon
                                  icon={["fas", "plus"]}
                                  className="font-size-sm"
                                />
                              </Button>
                            )}

                            {isRemoveAction && (
                              <Button
                                color="neutral-danger"
                                onClick={() => onRemoveAction(row)}
                                className="mx-1 shadow-none d-30 border-0 p-0 d-inline-flex align-items-center justify-content-center">
                                <FontAwesomeIcon
                                  icon={["fas", "minus-circle"]}
                                  className="font-size-sm"
                                />
                              </Button>
                            )}
                            {isPrintAction && (
                              <Button
                                color="neutral-info"
                                onClick={() => onPrintAction(row)}
                                className="mx-1 shadow-none d-30 border-0 p-0 d-inline-flex align-items-center justify-content-center">
                                <FontAwesomeIcon
                                  icon={["fas", "print"]}
                                  className="font-size-sm"
                                />
                              </Button>
                            )}
                          </td>
                        )
                      }
                    </tr>
                  ))}
                  <tr></tr>
                </tbody>
              </Table>
            ) : (
              <p className="bg-white text-center">No Data</p>
            )}
          </div>
        )}
        <PaginationComponent
          count={count}
          limit={limit}
          showPagination={showPagination}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
        />
      </Card >

      {/* Modal for modal type field, to show data in Modal */}
      < Modal
        zIndex={2001}
        centered
        size="md"
        isOpen={showModal}
        toggle={onToggleModal}
        contentClassName="modal-example-1 border-0 bg-transparent" >
        <Card className="card-box mb-4">
          <CardBody>
            <div style={{ padding: 10 }}>
              <code>
                {formatModalData ? JSON.stringify(modalData) : modalData}
              </code>
            </div>
            <Row style={{ padding: 15 }}>
              <Col>
                <Button
                  outline
                  color="link"
                  className="btn-link-dark"
                  onClick={onToggleModal}>
                  Close
                </Button>
              </Col>
            </Row>
          </CardBody>
        </Card>
      </Modal>

      <ConfirmDialog
        open={openConfirmationModal}
        confirmButtonLabel={deleteButtonLabel}
        confirmText={deleteConfirmLabel}
        subText="You cannot undo this operation."
        onConfirm={async () => {
          setConfirmationModal(false);
          await onDelete(rowToDelete);
        }}
        disabledCancel={disabledCancel}
        renderCustomChildren={renderCustomDeleteModal}
        onClose={() => setConfirmationModal(false)}
      />
    </>
  );
}

DataTable.propTypes = {
  filterType: PropTypes.string,
  loading: PropTypes.bool,
  isLoading: PropTypes.bool,
  tableLabel: PropTypes.string,
  column: PropTypes.array,
  data: PropTypes.array,
  count: PropTypes.number,
  currentPage: PropTypes.number,
  setCurrentPage: PropTypes.func,
  showActions: PropTypes.bool,
  onActionClick: PropTypes.func,
  onAdd: PropTypes.func,
  onEdit: PropTypes.func,
  onView: PropTypes.func,
  isView: PropTypes.bool,
  isEdit: PropTypes.bool,
  showAddButton: PropTypes.bool,
  addButtonLabel: PropTypes.string,
  selectableRow: PropTypes.bool,
  deleteConfirmLabel: PropTypes.string,
  isDelete: PropTypes.bool,
  onDelete: PropTypes.func,
  isSelected: PropTypes.array,
  buttons: PropTypes.array,
  showFilter: PropTypes.bool,
  isFilterActive: PropTypes.bool,
  onClearFilter: PropTypes.func,
  handleFilter: PropTypes.func,
  FilterComponent: PropTypes.any,
  isAddAction: PropTypes.bool,
  onAddAction: PropTypes.func,
  isRemoveAction: PropTypes.bool,
  onRemoveAction: PropTypes.func,
  onRowClick: PropTypes.func,
  showRemitted: PropTypes.bool,
  showNotRemitted: PropTypes.bool,
  showClearanceBtn: PropTypes.bool,
  onClearanceBtn: PropTypes.func,
  isPrintAction: PropTypes.bool,
  onPrintAction: PropTypes.func,
  component: PropTypes.element,
  showPagination: PropTypes.bool
};
